import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button } from '@material-ui/core';
import { CloudUpload as CloudUploadIcon } from '@material-ui/icons';

import { FetchError } from '../../../common/api/api';
import AppSnackbar from '../../../common/AppSnackbar';
import { useUserContext } from '../../../user/User.context';
import DocumentReferenceCard from '../../documents/components/DocumentReferenceCard';
import DocumentUploadDialog from '../../documents/components/DocumentUploadDialog';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { useFindTaskFiles, useUpdateTask } from '../Task.hooks';
import { Task } from '../Task.interface';

export interface TaskFileListCardProps {
  task: Task;
  disabled?: boolean;
  // fetch specific files
  fileReferences?: (number | { id: number; version: number })[];
  onChange?: (task: Task) => void;
  onError?: (error: FetchError) => void;
}

const TaskFileListCard: React.FC<TaskFileListCardProps> = props => {
  const { task, fileReferences, onChange, onError } = props;
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const [open, setOpen] = useState(false);
  const [order, setOrder] = useState<string>();
  const [direction, setDirection] = useState<'ASC' | 'DESC'>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const {
    taskFiles,
    isLoading: isFinding,
    findError,
    findTaskFiles
  } = useFindTaskFiles(task.id || 0);
  const { updatedTask, isUpdating, updateError, updateTask } = useUpdateTask(
    task.id
  );
  const can = useUserContext().permissions.file;
  const { isFieldDisabled } = useTaskEntityFields(task);
  const disabled = props.disabled || isFieldDisabled('taskFiles');
  const isLoading = isFinding || isUpdating;
  const error = findError || updateError;
  const files = useMemo(() => taskFiles || [], [taskFiles]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const findTaskFilesWithParams = useCallback(() => {
    findTaskFiles({ order, direction, fileReferences: fileReferences || [] });
    // refresh on change task.status
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, direction, fileReferences, task.status, findTaskFiles]);

  useEffect(() => {
    findTaskFilesWithParams();
  }, [updatedTask, findTaskFilesWithParams]);

  useEffect(() => {
    if (updatedTask) {
      onChangeRef.current?.(updatedTask);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (error) {
      onErrorRef.current?.(error);
    }
  }, [error]);

  return (
    <>
      <DocumentReferenceCard
        files={files}
        isLoading={isLoading}
        orderDirection={direction}
        disabled={disabled}
        setOrder={setOrder}
        setOrderDirection={setDirection}
        isActive={o => order === o}
        onAssigned={files => updateTask({ files })}
        onRemove={(removeId: number) => {
          updateTask({ files: files.filter(file => file.id !== removeId) });
        }}
        actions={
          <Button
            color='primary'
            variant='outlined'
            disabled={can.not.update || disabled}
            startIcon={<CloudUploadIcon />}
            onClick={() => setOpen(true)}
          >
            Upload Files
          </Button>
        }
      />

      <DocumentUploadDialog
        open={open}
        setOpen={setOpen}
        referenceTaskId={task.id}
        onDismiss={(progress, shouldFetch = true) => {
          const { success } = progress;
          if (success > 0) {
            setSuccessMessage(`Number of files uploaded: ${success}`);
            shouldFetch && findTaskFilesWithParams();
            onChangeRef.current?.({ ...task });
          }
        }}
      />

      <AppSnackbar
        severity='success'
        open={!!successMessage}
        onClose={() => setSuccessMessage(undefined)}
      >
        {successMessage}
      </AppSnackbar>
    </>
  );
};

export default TaskFileListCard;
